import type {
  CompositionV2BreakdownOptions,
  CompositionV2Filters,
} from '@revelio/data-access';

import type { SelectionListState } from '../../../../shared/selection-lists/types';

export const getCompositionBreakdown = ({
  compositionFilters,
  selectionLists,
  customSkillTree,
  customSkillSelections,
  customSkillDeepestLevel,
  usesStateBreakdownOverMsa = false,
}: {
  compositionFilters: CompositionV2Filters;
  selectionLists?: SelectionListState | null;
  customSkillTree?:
    | { id: string; name: string; depth: number; children?: unknown[] }[]
    | null;
  customSkillSelections?: string[];
  customSkillDeepestLevel?:
    | 'skill_0'
    | 'skill_1'
    | 'skill_2'
    | 'skill_3'
    | 'skill_4'
    | null;
  usesStateBreakdownOverMsa?: boolean;
}): CompositionV2BreakdownOptions => {
  /** ======== Geography ======== */
  const geography = ((): CompositionV2BreakdownOptions['Geography'] => {
    if (compositionFilters.region) {
      const countryIds = selectionLists?.country
        ?.filter((list) =>
          compositionFilters.region?.some((region) =>
            list.region?.includes(`${region}`)
          )
        )
        .map((list) => Number(list.id));

      return { all: false, selected: countryIds };
    }

    if (compositionFilters.country) {
      if (usesStateBreakdownOverMsa) {
        const stateIds = selectionLists?.state
          ?.filter((list) =>
            compositionFilters.country?.some((country) =>
              list.country?.includes(`${country}`)
            )
          )
          .map((list) => Number(list.id));

        return { all: false, selected: stateIds };
      } else {
        // MSA mode: step down to MSAs
        const msaIds = selectionLists?.msa
          ?.filter((list) =>
            compositionFilters.country?.some((country) =>
              list.country?.includes(`${country}`)
            )
          )
          .map((list) => Number(list.id));

        return { all: false, selected: msaIds };
      }
    }

    if (compositionFilters.state) {
      const cityIds = selectionLists?.city
        ?.filter((list) =>
          compositionFilters.state?.some((state) =>
            list.state?.includes(`${state}`)
          )
        )
        ?.map((list) => Number(list.id));

      if (Array.isArray(cityIds) && cityIds.length > 0) {
        return { all: false, selected: cityIds };
      }

      return { all: false, selected: compositionFilters.state };
    }

    // If msa selected, attempt to step into city using selectionLists.city if available
    if (compositionFilters.msa) {
      const cityIds = selectionLists?.city
        ?.filter((list) =>
          compositionFilters.msa?.some((msa) =>
            list.metro_area?.includes(`${msa}`)
          )
        )
        ?.map((list) => Number(list.id));

      if (Array.isArray(cityIds) && cityIds.length > 0) {
        return { all: false, selected: cityIds };
      }

      return { all: false, selected: compositionFilters.msa };
    }

    if (compositionFilters.city) {
      return { all: false, selected: compositionFilters.city };
    }

    const regionIds = selectionLists?.region?.map((list) => Number(list.id));

    return { all: false, selected: regionIds };
  })();

  /** ======== Industry ======== */
  const rics = ((): CompositionV2BreakdownOptions['Rics'] => {
    if (compositionFilters.rics_k10) {
      const ricsK50Ids = selectionLists?.rics_k400
        ?.filter((list) =>
          compositionFilters.rics_k10?.some((rics_k10) =>
            list.rics_k10?.includes(`${rics_k10}`)
          )
        )
        ?.map((list) => Number(list.id));

      return { all: false, selected: ricsK50Ids };
    }

    if (compositionFilters.rics_k400) {
      return { all: false, selected: compositionFilters.rics_k400 };
    }

    const ricsK10Ids = selectionLists?.rics_k10?.map((list) => Number(list.id));

    return { all: false, selected: ricsK10Ids };
  })();

  /** ======== Role ======== */
  const role = ((): CompositionV2BreakdownOptions['Role'] => {
    // role_k7 maps to role_k50
    if (
      compositionFilters?.custom_role?.entities?.some(
        (entity) => entity?.levelId === 'role_k7'
      )
    ) {
      const roleK50Ids = selectionLists?.role_k50
        ?.filter((k50) =>
          compositionFilters?.custom_role?.entities
            ?.map((entity) => entity?.id)
            ?.includes(k50.role_k7)
        )
        ?.map((k50) => Number(k50.id));

      return { all: false, selected: roleK50Ids };
    }

    // role_k150 maps to role_k500
    if (
      compositionFilters?.custom_role?.entities?.some(
        (entity) => entity?.levelId === 'role_k150'
      )
    ) {
      const roleK500Ids = selectionLists?.role_k500
        ?.filter((k500) =>
          compositionFilters?.custom_role?.entities
            ?.map((entity) => entity?.id)
            ?.includes(k500.role_k150)
        )
        ?.map((k500) => Number(k500.id));

      return { all: false, selected: roleK500Ids };
    }

    // role_k1500 maps to role_k5000
    if (
      compositionFilters?.custom_role?.entities?.some(
        (entity) => entity?.levelId === 'role_k1500'
      )
    ) {
      const roleK5000Ids = selectionLists?.role_k5000
        ?.filter((k5000) =>
          compositionFilters?.custom_role?.entities
            ?.map((entity) => entity?.id)
            ?.includes(k5000.role_k1500)
        )
        ?.map((k5000) => Number(k5000.id));

      return { all: false, selected: roleK5000Ids };
    }

    // role_k15000 returns self
    if (
      compositionFilters?.custom_role?.entities?.some(
        (entity) => entity?.levelId === 'role_k15000'
      )
    ) {
      const roleK15000Ids = compositionFilters?.custom_role?.entities?.map(
        (entity) => Number(entity?.id)
      );

      return { all: false, selected: roleK15000Ids };
    }

    const roleK7Ids = selectionLists?.role_k7?.map((role) => Number(role.id));
    return { all: false, selected: roleK7Ids };
  })();

  /** ======== Skill ======== */
  const skill = ((): CompositionV2BreakdownOptions['Skill'] => {
    // CUSTOM_SKILL: restrict breakdown to the children of the selected nodes
    // skill_0 -> select ids of level 1 children of selected skill_0 nodes
    // skill_1 -> select ids of level 2 children of selected skill_1 nodes
    // skill_2 -> select ids of level 3 children of selected skill_2 nodes
    // skill_3 -> select ids of level 4 children of selected skill_3 nodes
    // skill_4 -> select ids of the selected leaves (level 5)

    type SkillNode = { id: string; name: string; children?: SkillNode[] };

    const tree = (customSkillTree as SkillNode[] | null) ?? null;
    const selections = Array.isArray(customSkillSelections)
      ? (customSkillSelections as string[])
      : [];

    const findNodeById = (id: string): SkillNode | null => {
      if (!tree) return null;
      const stack: SkillNode[] = [...tree];
      while (stack.length) {
        const node = stack.pop();
        if (!node) continue;
        if (node.id === id) return node;
        if (node.children && node.children.length) {
          stack.push(...node.children);
        }
      }
      return null;
    };

    const toNum = (nestedId: string): number | null => {
      const last = nestedId.split('_').slice(-1)[0];
      const n = Number(last);
      return Number.isFinite(n) ? n : null;
    };

    const selected: number[] = [];

    // When no skill filter is present, return all skill_0 (level 1) IDs
    if (!customSkillDeepestLevel && tree) {
      const allSkill0Ids: number[] = [];
      tree.map((rootNode) => {
        const n = toNum(rootNode.id);
        if (n !== null) allSkill0Ids.push(n);
        return n;
      });

      return { all: false, selected: Array.from(new Set(allSkill0Ids)) };
    }

    if (customSkillDeepestLevel === 'skill_0') {
      selections.forEach((sel) => {
        const root = findNodeById(sel);
        const lvl1 = root?.children || [];
        (lvl1 as SkillNode[]).forEach((c) => {
          const n = toNum(c.id);
          if (n !== null) selected.push(n);
        });
      });
      return { all: false, selected: Array.from(new Set(selected)) };
    }

    if (customSkillDeepestLevel === 'skill_1') {
      selections.forEach((sel) => {
        const node = findNodeById(sel);
        const lvl2 = node?.children || [];
        (lvl2 as SkillNode[]).forEach((c) => {
          const n = toNum(c.id);
          if (n !== null) selected.push(n);
        });
      });
      return { all: false, selected: Array.from(new Set(selected)) };
    }

    if (customSkillDeepestLevel === 'skill_2') {
      selections.forEach((sel) => {
        const node = findNodeById(sel);
        const lvl3 = node?.children || [];
        (lvl3 as SkillNode[]).forEach((c) => {
          const n = toNum(c.id);
          if (n !== null) selected.push(n);
        });
      });
      return { all: false, selected: Array.from(new Set(selected)) };
    }

    if (customSkillDeepestLevel === 'skill_3') {
      selections.forEach((sel) => {
        const node = findNodeById(sel);
        const lvl5 = node?.children || [];
        (lvl5 as SkillNode[]).forEach((c) => {
          const n = toNum(c.id);
          if (n !== null) selected.push(n);
        });
      });
      return { all: false, selected: Array.from(new Set(selected)) };
    }

    if (customSkillDeepestLevel === 'skill_4') {
      selections.forEach((sel) => {
        const n = toNum(sel);
        if (n !== null) selected.push(n);
      });
      return { all: false, selected: Array.from(new Set(selected)) };
    }

    return undefined;
  })();

  const education = ((): CompositionV2BreakdownOptions['Education'] => {
    if (compositionFilters.highest_degree) {
      return { all: false, selected: compositionFilters.highest_degree };
    }

    return undefined;
  })();

  const ethnicity = ((): CompositionV2BreakdownOptions['Ethnicity'] => {
    if (compositionFilters.ethnicity) {
      return { all: false, selected: compositionFilters.ethnicity };
    }

    return undefined;
  })();

  const gender = ((): CompositionV2BreakdownOptions['Gender'] => {
    if (compositionFilters.gender) {
      return { all: false, selected: compositionFilters.gender };
    }

    return undefined;
  })();

  const seniority = ((): CompositionV2BreakdownOptions['Seniority'] => {
    if (compositionFilters.seniority) {
      return { all: false, selected: compositionFilters.seniority };
    }

    return undefined;
  })();

  return {
    ...(role && { Role: role }),
    ...(geography && { Geography: geography }),
    ...(rics && { Rics: rics }),
    ...(skill && { Skill: skill }),
    ...(education && { Education: education }),
    ...(ethnicity && { Ethnicity: ethnicity }),
    ...(gender && { Gender: gender }),
    ...(seniority && { Seniority: seniority }),
  };
};
